import {
  CheckIcon,
  CriticalIcon,
  Flex,
  SimpleSpinner,
  Tag,
  Text,
  WarningIcon,
} from '@buffer-mono/popcorn'
import type { ListItem } from './PostItems'
import styles from './PostItem.module.css'
import { clsx } from 'clsx'
import { useState } from 'react'

export const PostItem = ({
  item,
  index,
}: {
  item: ListItem
  index: number
}): JSX.Element => {
  const [hasImageError, setHasImageError] = useState(false)
  return (
    <li className={styles.listItem}>
      {item.status === 'valid' || item.status === 'invalid' ? (
        <span className={clsx(styles.itemPrefixContainer, styles.numberBadge)}>
          {index + 1}
        </span>
      ) : item.status === 'loading' ? (
        <span className={styles.itemPrefixContainer}>
          <SimpleSpinner />
        </span>
      ) : item.status === 'success' ? (
        <span className={clsx(styles.itemPrefixContainer, styles.successBadge)}>
          <CheckIcon color="inverted" size="small" />
        </span>
      ) : item.status === 'error' ? (
        <span className={clsx(styles.itemPrefixContainer, styles.errorBadge)}>
          <CriticalIcon color="inverted" size="small" />
        </span>
      ) : null}
      <div className={styles.listItemRow}>
        <Flex
          className={styles.listItemContent}
          align="center"
          gap="xs"
          aria-invalid={item.status === 'error'}
          data-status={item.status}
        >
          <Flex direction="row" gap="xs" className={styles.textWrapper}>
            <Text className={styles.itemText} truncate>
              {item.data.text}
            </Text>
          </Flex>
          {item.data.tags.length > 0 && (
            <Flex
              direction="row"
              gap="xs"
              align="center"
              className={styles.tagsWrapper}
            >
              {item.data.tags.slice(0, 2).map((tag) => (
                <Tag key={tag.id} color={tag.color} className={styles.tag}>
                  {tag.name}
                </Tag>
              ))}
              {item.data.tags.length > 2 && (
                <Tag color="subtle" className={styles.tag}>
                  {`+${item.data.tags.length - 2}`}
                </Tag>
              )}
            </Flex>
          )}
          {item.data?.media?.photo && !hasImageError && (
            <Flex justify="end" className={styles.imageCell}>
              <img
                className={styles.itemImage}
                src={item.data.media.photo}
                alt=""
                onError={() => setHasImageError(true)}
              />
            </Flex>
          )}
          {item.data?.media?.photo && hasImageError && (
            <Flex justify="end" className={styles.imageCell}>
              <Flex
                align="center"
                justify="center"
                className={styles.imageErrorCell}
              >
                <img className={styles.imageError} alt=" " />
              </Flex>
            </Flex>
          )}
        </Flex>
        {(!!item.errors?.length || !!item.warnings?.length) && (
          <Flex direction="column" gap="xs">
            {item.warnings?.map((warning) => (
              <Flex key={warning} direction="row" gap="2xs" align="center">
                <WarningIcon size="xsmall" color="warning" />
                <Text size="sm" color="warning">
                  {warning}
                </Text>
              </Flex>
            ))}
            {item.errors?.map((error) => (
              <Flex key={error} direction="row" gap="2xs" align="center">
                <CriticalIcon size="xsmall" color="critical" />
                <Text size="sm" color="critical">
                  {error}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </div>
    </li>
  )
}
